import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Empty,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi, type AnalyticsDateFilters } from '@/services/api';
import { apiErrorMessage } from '@utils/apiError';

const { Title, Text } = Typography;

interface MatchBreakdown {
  byMatchType: Array<{ matchType: string; count: number; percentage: number }>;
  customMatchByOvers: Array<{ overs: number; count: number; percentage: number }>;
  totalMatches: number;
}

interface Props {
  period: AnalyticsDateFilters['period'];
  /** YYYY-MM-DD, only for the custom period. */
  startDate?: string;
  endDate?: string;
}

const BreakdownBody: React.FC<Props> = ({ period, startDate, endDate }) => {
  const [data, setData] = useState<MatchBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const params: AnalyticsDateFilters =
      period === 'custom' ? { period, startDate, endDate } : { period };
    adminApi
      .getMatchBreakdown(params)
      .then((res) => live && setData(res.data as MatchBreakdown))
      .catch((err) => live && setError(apiErrorMessage(err, 'Failed to load the match breakdown.')))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [period, startDate, endDate, attempt]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message={error}
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!data || loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }
  return (
    <>
      <Tag style={{ marginBottom: 12 }}>{data.totalMatches.toLocaleString()} matches in range</Tag>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Title level={5}>By match type</Title>
          {data.byMatchType.map((row) => (
            <div key={row.matchType} style={{ marginBottom: 8 }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text>{row.matchType}</Text>
                <Text strong>
                  {row.count.toLocaleString()} ({row.percentage.toFixed(1)}%)
                </Text>
              </Space>
              <Progress percent={row.percentage} showInfo={false} size="small" />
            </div>
          ))}
        </Col>
        <Col xs={24} md={12}>
          <Title level={5}>Custom-match overs split</Title>
          {data.customMatchByOvers.length === 0 ? (
            <Empty description="No custom matches in range" />
          ) : (
            data.customMatchByOvers.map((row) => (
              <div key={row.overs} style={{ marginBottom: 8 }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{row.overs}-over</Text>
                  <Text strong>
                    {row.count.toLocaleString()} ({row.percentage.toFixed(1)}%)
                  </Text>
                </Space>
                <Progress
                  percent={row.percentage}
                  showInfo={false}
                  size="small"
                  strokeColor="#52c41a"
                />
              </div>
            ))
          )}
        </Col>
      </Row>
    </>
  );
};

/**
 * Matches by type and custom-match overs for the page's date range. Moved here from the Dashboard (removed
 * 26 Oct 2026). It loads only when opened (the report takes ~18 s on production), then follows the range.
 */
export const MatchBreakdownPanel: React.FC<Props> = (props) => (
  <Collapse
    style={{ marginTop: 16 }}
    items={[
      {
        key: 'match-breakdown',
        label: (
          <span>
            Match breakdown{' '}
            <Text type="secondary">
              matches by type and custom-match overs, for the range above
            </Text>
          </span>
        ),
        children: <BreakdownBody {...props} />,
      },
    ]}
  />
);
